// Libs
import type { AppProps } from 'next/app';
import { SessionProvider } from 'next-auth/react';
import { useRouter } from 'next/router';
import { Session } from 'next-auth';
import React, { ReactElement, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pageview } from 'app-shared/lib/utils/analytics.utils';
import { useDispatch } from 'react-redux';
import log from 'loglevel';
import { Analytics } from '@vercel/analytics/react';
// Material ui
import { defaultTheme, ThemeProvider, CssBaseline, DialogProvider as LeftPanelProvider } from 'ui-components';
import createEmotionCache from 'app-shared/lib/utils/emotion.utils';
import { CacheProvider, EmotionCache } from '@emotion/react';
// App
import wrapper from '@/redux/store';
import setupAxiosInterceptors from 'app-shared/lib/api/utils/axios-interceptors';
import initPwa from 'app-shared/lib/utils/init/init-pwa';
import Auth from '@/fragments/auth';
import { NextComponentType, NextPageContext } from 'next';
import { AuthConfig } from '@/fragments/auth/auth.interfaces';
import { setPreviousRoute } from '@/redux/router-history.slice';
import MaintenancePage from '@/fragments/user-access/maintenance';
import { USER_ACCESS_PAGES_URLS } from '@/fragments/user-access/user-access.constants';
import RouteLoading from '@/components/navigation/route-loading/route-loading';
import BottomNavBar from '@/layout/shared/bottom-nav-bar/bottom-nav-bar';
import { LOG_LEVEL, IS_MAINTENANCE_MODE } from '@/config/app.config';
import AccountProvider from './_shared/account/context/account.provider';
import { LeftPanelContext } from '@/layout/shop-administration/left-panel/left-panel-context';

type CustomNextComponentType<P = unknown> = Partial<NextComponentType<NextPageContext, unknown, P>> & {
  authConfig: AuthConfig;
};

type CustomAppProps<P = unknown> = Partial<
  AppProps<{
    session: Session;
  }>
> & {
  Component: CustomNextComponentType<P>;
  emotionCache: EmotionCache;
};

const ROUTE_CHANGE_COMPLETE_EVENT = 'routeChangeComplete';
const BEFORE_HISTORY_CHANGE_EVENT = 'beforeHistoryChange';
const HISTORY_CHANGE_IGNORED_ROUTES = [USER_ACCESS_PAGES_URLS['login']];

// Enable API mocking in all environments except production.
if (process.env.NODE_ENV !== 'production') {
  import('../mocks');
}

setupAxiosInterceptors();

const clientSideEmotionCache: EmotionCache = createEmotionCache();

const App = ({ Component, pageProps, emotionCache = clientSideEmotionCache }: CustomAppProps): ReactElement => {
  let pageComponent;
  const { session } = pageProps;
  const router = useRouter();
  const dispatch = useDispatch();

  /**
   * Notify google analytics from page view on route change complete.
   */
  const handleRouteChangeComplete = (url: string) => {
    pageview(url);

    const jssStyles = document.querySelector('#jss-server-side');

    if (!jssStyles) return;

    jssStyles.parentElement.removeChild(jssStyles);
  };

  /**
   * Store previous route in redux.
   */
  const handleBeforeHistoryChange = (url: string) => {
    const [nextRoutePath] = url?.split('?') || [];
    const currentRoutePath = router.asPath;

    if (
      nextRoutePath !== currentRoutePath &&
      !HISTORY_CHANGE_IGNORED_ROUTES.includes(nextRoutePath) &&
      !HISTORY_CHANGE_IGNORED_ROUTES.includes(currentRoutePath)
    ) {
      dispatch(setPreviousRoute(currentRoutePath));
    }
  };

  /**
   * Init app
   */
  useEffect(() => {
    log.setLevel(LOG_LEVEL);

    initPwa();
  }, []);

  /**
   * Init app
   */
  useEffect(() => {
    router.events.on(ROUTE_CHANGE_COMPLETE_EVENT, handleRouteChangeComplete);
    router.events.on(BEFORE_HISTORY_CHANGE_EVENT, handleBeforeHistoryChange);

    return () => {
      router.events.off(ROUTE_CHANGE_COMPLETE_EVENT, handleRouteChangeComplete);
      router.events.off(BEFORE_HISTORY_CHANGE_EVENT, handleBeforeHistoryChange);
    };
  }, [router.pathname]);

  if (IS_MAINTENANCE_MODE) {
    pageComponent = MaintenancePage();
  } else if (Component.authConfig) {
    pageComponent = (
      <Auth config={Component.authConfig}>
        <Component {...pageProps} />
      </Auth>
    );
  } else {
    pageComponent = <Component {...pageProps} />;
  }

  const AppTemplate = (
    <ThemeProvider theme={defaultTheme}>
      <CacheProvider value={emotionCache}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <Analytics />
          <SessionProvider session={session} refetchOnWindowFocus={true}>
            <AccountProvider>
              <LeftPanelProvider shouldOpenOnInit={false} context={LeftPanelContext}>
                {pageComponent}
              </LeftPanelProvider>
            </AccountProvider>
            <BottomNavBar />
          </SessionProvider>
          <RouteLoading />
        </LocalizationProvider>
      </CacheProvider>
    </ThemeProvider>
  );

  return AppTemplate;
};

export default wrapper.withRedux(App);
